import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReusableCheckbox } from '../checkbox-reusable/checkbox-reusable';

/** One column the picker can show or hide. */
export interface ColumnPickerOption<K extends string = string> {
  readonly key: K;
  readonly label: string;
}

/**
 * Dropdown that toggles which data columns a table shows. Controlled: it renders
 * `visibleKeys` and emits the full next set, in `columns` order, on every change.
 * The last visible column can't be hidden, so the table never goes blank.
 */
@Component({
  selector: 'app-column-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, ReusableCheckbox],
  templateUrl: './column-picker.html',
})
export class ColumnPickerComponent<K extends string = string> {
  @Input() columns: readonly ColumnPickerOption<K>[] = [];
  @Input() visibleKeys: readonly K[] = [];

  @Output() visibleKeysChange = new EventEmitter<K[]>();

  protected isOpen = false;
  private readonly hostElement = inject(ElementRef<HTMLElement>);

  protected get visibleCount(): number {
    return this.visibleKeys.length;
  }

  protected isVisible(key: K): boolean {
    return this.visibleKeys.includes(key);
  }

  protected isLocked(key: K): boolean {
    return this.visibleKeys.length === 1 && this.isVisible(key);
  }

  protected toggle(): void {
    this.isOpen = !this.isOpen;
  }

  protected onToggleColumn(key: K, checked: boolean): void {
    if (!checked && this.isLocked(key)) {
      return;
    }

    // Rebuild from the canonical column order so keys never drift out of order.
    const next = this.columns
      .map((column) => column.key)
      .filter((columnKey) => (columnKey === key ? checked : this.isVisible(columnKey)));

    this.visibleKeysChange.emit(next);
  }

  protected selectAll(): void {
    this.visibleKeysChange.emit(this.columns.map((column) => column.key));
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.hostElement.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }
}
